import clsx from 'clsx';
import { useState } from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

import { useCopyFeedback } from '@/hooks/use-copy-feedback';
import { AssistantMessageFeedback } from '@/components/assistant-message-feedback';

import { CopyFeedbackIcon } from './copy-feedback-icon';
import type { ChatMessageProps } from './types';

import { Tooltip } from '@flintmint/web-shared/components/tooltip';

import styles from './chat-message.module.scss';

import LogoDark from '@/assets/icons/logo-dark.svg?react';

const DISALLOWED_ELEMENTS = ['img', 'iframe', 'video', 'audio', 'script', 'style'];

export const ChatMessage = (props: ChatMessageProps) => {
  const { message, requestId, documentKind } = props;

  const [hideTooltip, setHideTooltip] = useState(false);

  const { copied, copy } = useCopyFeedback(message.text, {
    onReset: () => setHideTooltip(true),
  });

  const isUser = message.role === 'user';

  const handleCopy = async () => {
    if (await copy()) {
      setHideTooltip(false);
    }
  };

  return (
    <div
      className={clsx(styles.chatMessage, {
        [styles.user]: isUser,
        [styles.assistant]: !isUser,
      })}
    >
      {!isUser && (
        <div className={styles.chatMessageAvatar}>
          <LogoDark style={{ width: 13, height: 13 }} />
        </div>
      )}
      <div className={styles.chatMessageContent}>
        <div className={styles.chatMessageBubble}>
          {isUser ? (
            message.text
          ) : (
            <Markdown skipHtml remarkPlugins={[remarkBreaks]} disallowedElements={DISALLOWED_ELEMENTS}>
              {message.text}
            </Markdown>
          )}
        </div>
        <div className={styles.chatMessageFooter}>
          <time className={styles.timestamp} dateTime={new Date(message.createdAt).toISOString()}>
            {formatChatMessageTime(message.createdAt)}
          </time>
          <div className={styles.chatMessageActions}>
            <div onMouseEnter={() => setHideTooltip(false)}>
              <Tooltip content={hideTooltip ? undefined : copied ? 'Copied' : 'Copy'} alwaysOpen={copied}>
                <button
                  type="button"
                  onClick={handleCopy}
                  className={styles.actionButton}
                  aria-label={copied ? 'Copied' : 'Copy message'}
                >
                  <span className={styles.copyActionIcon}>
                    <CopyFeedbackIcon copied={copied} size={20} />
                  </span>
                </button>
              </Tooltip>
            </div>
            {!isUser && <AssistantMessageFeedback requestId={requestId} messageId={message.id} documentKind={documentKind} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatChatMessageTime = (createdAt: number) => {
  return new Date(createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};
